/**
 * Banner waitlist form — primary capture point on every marketing page.
 *
 * Behavior:
 *   - On DOMContentLoaded, find #bannerWaitlistForm. No-op if absent.
 *   - On submit, validate email + POST /api/v1/waitlist with
 *     { email, source: "banner", language, … }.
 *   - On success: hide form, show "✓ You're on the list" message,
 *     persist `bp-banner-waitlist-submitted=1` in localStorage so the
 *     banner stays hidden on subsequent visits.
 *   - On failure: shake the form, toggle .is-invalid on the email input,
 *     surface the error region.
 *   - Dismiss button hides the banner and persists `bp-launch-bar=dismissed`.
 *   - On boot, if either flag is set, hide the banner.
 *
 * Wired up by `app/frontend/js/components/index.ts`, which every entrypoint
 * that renders the shared navbar already imports.
 */

import { api_v1_waitlist_path } from '../shared/api-routes';
import { trackWaitlistSubscribe } from '../shared/analytics.ts';
import { isAuthenticated } from '../shared/auth.ts';

const STORAGE_SUBMITTED = 'bp-banner-waitlist-submitted';
const STORAGE_DISMISSED = 'bp-launch-bar';
const SUCCESS_AUTOHIDE_MS = 5000;

function safeGet(key: string): string | null {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    /* private mode / quota — ignore */
  }
}

function getSource(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('utm_source') || params.get('ref') || 'banner';
  } catch {
    return 'banner';
  }
}

function getLanguage(): string {
  const docLang = document.documentElement.lang;
  if (docLang) return docLang.split('-')[0].toLowerCase();
  return 'en';
}

function isValidEmail(value: string): boolean {
  // Lightweight RFC-5322-ish guard. Real validation lives server-side.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function hideBanner(banner: HTMLElement): void {
  banner.classList.add('is-dismissed');
}

function triggerShake(form: HTMLFormElement, errorEl: HTMLElement | null): void {
  form.classList.remove('is-shake');
  // Force a reflow so the animation restarts on repeated invalid submits.
  void form.offsetWidth;
  form.classList.add('is-shake');
  if (errorEl) errorEl.hidden = false;
}

export function initBannerWaitlist(): void {
  const banner = document.querySelector<HTMLElement>('.early-access-banner');
  if (!banner) return;

  // The banner promotes the waitlist — never show it to a logged-in
  // user (they're already past the gate). Same auth source as bp-user-menu.
  if (isAuthenticated()) {
    hideBanner(banner);
    return;
  }

  // Suppress the banner if user already submitted or dismissed.
  if (safeGet(STORAGE_SUBMITTED) === '1' || safeGet(STORAGE_DISMISSED) === 'dismissed') {
    hideBanner(banner);
    return;
  }

  const form = banner.querySelector<HTMLFormElement>('#bannerWaitlistForm');
  if (!form) return;

  const emailInput = form.querySelector<HTMLInputElement>('#bannerWaitlistEmail');
  const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  const dismissBtn = form.querySelector<HTMLButtonElement>('[data-action="dismiss"]');
  const successEl = banner.querySelector<HTMLElement>('#bannerWaitlistSuccess');
  const errorEl = banner.querySelector<HTMLElement>('#bannerWaitlistError');

  if (dismissBtn) {
    dismissBtn.addEventListener('click', (e) => {
      e.preventDefault();
      safeSet(STORAGE_DISMISSED, 'dismissed');
      hideBanner(banner);
    });
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!emailInput || !submitBtn) return;

    const email = emailInput.value.trim();
    emailInput.classList.remove('is-invalid');
    if (errorEl) errorEl.hidden = true;

    if (!isValidEmail(email)) {
      emailInput.classList.add('is-invalid');
      triggerShake(form, errorEl);
      return;
    }

    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = '...';

    fetch(api_v1_waitlist_path(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        source: getSource(),
        language: getLanguage(),
      }),
    })
      .then((resp) => {
        if (resp.ok) return resp.json();
        return resp.json().then((err) => { throw err; });
      })
      .then(() => {
        try { trackWaitlistSubscribe(getSource()); } catch { /* analytics best-effort */ }
        safeSet(STORAGE_SUBMITTED, '1');
        form.hidden = true;
        if (successEl) successEl.hidden = false;
        // Auto-dismiss the banner after the user has had a chance to read.
        window.setTimeout(() => hideBanner(banner), SUCCESS_AUTOHIDE_MS);
      })
      .catch(() => {
        submitBtn.disabled = false;
        if (originalText) submitBtn.textContent = originalText;
        emailInput.classList.add('is-invalid');
        triggerShake(form, errorEl);
      });
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBannerWaitlist, { once: true });
  } else {
    initBannerWaitlist();
  }
}
